import { useNavigate, Link } from 'react-router-dom';
import { BankAccountSetup } from '../components/BankAccountSetup';
import { CardSetup } from '../components/CardSetup';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { ParentPaymentPreferences } from '../types/payment-preferences';
import { useLabels } from '../config/useLabels';
import { useUrlTabState } from '../hooks/useUrlTabState';

type MethodTab = 'ach' | 'card';
const METHOD_TABS: readonly MethodTab[] = ['ach', 'card'];

export default function AddPaymentMethod() {
  const navigate = useNavigate();
  const t = useLabels();
  const [activeTab, setActiveTab] = useUrlTabState<MethodTab>('tab', METHOD_TABS, 'ach');

  const upsertPrefs = useMutation({
    mutationFn: async (
      input: Partial<Pick<ParentPaymentPreferences, 'default_payment_method'>> & {
        default_bank_account_setup_intent_id?: string | null;
      }
    ) => {
      const { data, error } = await supabase.rpc('upsert_parent_payment_preferences', {
        p_default_payment_method: input.default_payment_method ?? null,
        p_autopay_enabled: null,
        p_default_bank_account_setup_intent_id:
          input.default_bank_account_setup_intent_id ?? null,
      });
      if (error) throw error;
      return data as ParentPaymentPreferences;
    },
    onSuccess: () => navigate('/account'),
  });

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg sm:text-xl font-semibold">{t('action.addPaymentMethod')}</h1>
        <Link to="/account" className="text-sm text-blue-600 hover:text-blue-800">Back to Account</Link>
      </div>

      {/* Tabs */}
      <div className="mb-4">
        <div className="inline-flex rounded-md shadow-sm border overflow-hidden">
          <button
            className={`px-4 py-2 text-sm font-medium ${activeTab === 'ach' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('ach')}
          >
            Bank Account (ACH)
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-l ${activeTab === 'card' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('card')}
          >
            Credit/Debit Card
          </button>
        </div>
      </div>

      {activeTab === 'ach' && (
        <div className="mb-6">
          <h2 className="text-md font-medium mb-2">Bank Account (ACH)</h2>
          <p className="text-sm text-gray-600 mb-3">Connect your bank account for secure, low-cost ACH payments.</p>
          <BankAccountSetup
            disableRedirect
            onSetupComplete={async (setupIntentId) => {
              // Map Stripe setup_intent_id (text) to our DB UUID id
              let dbSetupId: string | null = null;
              try {
                const { data } = await supabase
                  .from('bank_account_setup_intents')
                  .select('id')
                  .eq('setup_intent_id', setupIntentId)
                  .maybeSingle();
                dbSetupId = data?.id ?? null;
              } catch (_) {
                dbSetupId = null;
              }

              upsertPrefs.mutate({
                default_payment_method: 'bank_account',
                default_bank_account_setup_intent_id: dbSetupId,
              });
            }}
            onError={() => {}}
          />
        </div>
      )}

      {activeTab === 'card' && (
        <div className="mt-2 p-4 border rounded-md bg-gray-50">
          <h2 className="text-md font-medium mb-3">Credit/Debit Card</h2>
          <CardSetup
            onComplete={() => {
              upsertPrefs.mutate({ default_payment_method: 'card' });
            }}
            onError={() => {}}
          />
        </div>
      )}
    </div>
  );
}


